import { action, computed, flow, makeObservable, observable } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { AxiosRequestConfig } from 'axios';
import { OrdersHistoryTableColumns, TOrder, orderCast, ordersCast } from './types';
import downloadFile from './helpers';
import { SORTING_DIRECTION } from 'src/constants/main';
import client from 'src/services/client';
import { LoadingStatus, TPagination, paginationCast } from 'src/types/main';
import { TEventMessage, TOrderCRUDEvents, TOrder as TSocketOrder } from 'src/types/orders';
import { AdvancedFilterStore } from 'src/view/components/AdvancedFilter';
import { AdvancedFilterProperties } from 'src/view/components/AdvancedFilter/types';
import { DateRanges } from '~/constants/dateTime';
import logger from '~/services/logger';
import wms from '~/services/wms';

type TSortingOrders = {
  column: OrdersHistoryTableColumns | null;
  direction: SORTING_DIRECTION;
};
type TGetOrdersParams = {
  page?: number;
  per_page?: number;
  sort?: {
    field?: string;
    direction: string;
  };
  search?: string;
  format?: string;
  filters: {
    branches?: string[];
    payment_types?: string[];
    status?: string[];
    delivery_types?: string[];
    order_channels?: string[];
    date_range?: {
      start: string;
      end: string;
    };
    time_range?: {
      start: string;
      end: string;
    };
  };
};

const sortFieldByColumn = (column: OrdersHistoryTableColumns) => {
  switch (column) {
    case OrdersHistoryTableColumns.OrderID:
      return 'order_key';
    case OrdersHistoryTableColumns.BranchName:
      return 'branch';
    case OrdersHistoryTableColumns.Status:
      return 'status';
    case OrdersHistoryTableColumns.CreatedAt:
      return 'created_at';
    case OrdersHistoryTableColumns.TotalPrice:
      return 'total';
    default:
      break;
  }
};

const defaultSorting = { column: null, direction: SORTING_DIRECTION.ASCENDING };
const defaultPagination = { currentPage: 1, lastPage: 0, totalRows: 0, perPage: 0 };

export class OrdersHistoryStore {
  constructor() {
    makeObservable(this);
  }

  isCallCenterScreen = false;

  hydrate = (uniqueKey: string = '') => {
    return Promise.all([
      makePersistable(this, {
        name: 'orders_history_store_' + uniqueKey,
        properties: ['pagination', 'sorting', 'searchValue'],
        storage: window.localStorage,
      }),
      this.advancedFilter.hydrate(uniqueKey),
    ]);
  };

  advancedFilter: AdvancedFilterStore = new AdvancedFilterStore({
    name: 'orders_history',
    parentStore: this,
    properties: [
      AdvancedFilterProperties.Branch,
      AdvancedFilterProperties.Status,
      AdvancedFilterProperties.Date,
      AdvancedFilterProperties.Time,
      AdvancedFilterProperties.OrderType,
      AdvancedFilterProperties.PaymentMethod,
      AdvancedFilterProperties.OrderingChannel,
    ],
    presetDates: [
      DateRanges.TodayUntilNow,
      DateRanges.Yesterday,
      DateRanges.ThisWeekUntilNow,
      DateRanges.LastWeek,
      DateRanges.ThisMonthUntilNow,
      DateRanges.LastMonth,
      DateRanges.ThisYearUntilNow,
      DateRanges.LastYear,
    ],
    defaultDatePreset: DateRanges.ThisWeekUntilNow,
  });

  @observable private _orders = new Map<string, TOrder>();

  @observable private _newOrders = new Map<string, TOrder>();
  @observable public ordersLoadingStatus: LoadingStatus = LoadingStatus.Init;
  @observable public exportLoadingStatus: LoadingStatus = LoadingStatus.Init;

  @observable public pagination: TPagination = defaultPagination;

  @observable public sorting: TSortingOrders = defaultSorting;
  @observable public perPage: number = 10;
  @observable public searchValue: string = '';

  ordersGetAbortController = new AbortController();
  ordersExportAbortController = new AbortController();

  ordersGettingTimeoutId: NodeJS.Timeout | undefined = undefined;

  private _makeFetchOrdersParams = (format: 'list' | 'csv' | 'url' = 'list', page?: number) => {
    const params: TGetOrdersParams =
      format === 'list'
        ? {
            page,
            per_page: this.perPage,
            filters: {},
            format,
          }
        : {
            filters: {},
            format,
          };

    if (this.sorting.column) {
      params.sort = {
        field: sortFieldByColumn(this.sorting.column),
        direction: this.sorting.direction.toUpperCase(),
      };
    }

    if (this.searchValue) {
      params.search = this.searchValue;
    } else {
      params.filters.branches = this.advancedFilter.values.BRANCH;
      params.filters.payment_types = this.advancedFilter.values.PAYMENT_METHOD;
      params.filters.delivery_types = this.advancedFilter.values.ORDER_TYPE;
      params.filters.order_channels = this.advancedFilter.values.ORDERING_CHANNEL;
      params.filters.status = this.advancedFilter.values.STATUS;

      if (this.advancedFilter.dateValues?.from && this.advancedFilter.dateValues?.to) {
        params.filters.date_range = {
          start: this.advancedFilter.dateValues.from,
          end: this.advancedFilter.dateValues.to,
        };
      }
      if (this.advancedFilter.values.TIME?.from && this.advancedFilter.values.TIME?.to) {
        params.filters.time_range = {
          start: this.advancedFilter.values.TIME.from,
          end: this.advancedFilter.values.TIME.to,
        };
      }
    }

    return params;
  };

  @action private _getOrders = flow(function* (this: OrdersHistoryStore, page: number): any {
    this.ordersGetAbortController.abort();
    const ordersGetAbortController = new AbortController();
    this.ordersGetAbortController = ordersGetAbortController;

    try {
      this.ordersLoadingStatus = LoadingStatus.Loading;
      const params = this._makeFetchOrdersParams('list', page);

      const response = yield client.get('/v3/orders/history', { params, signal: this.ordersGetAbortController.signal });
      const { data = [], pagination } = response;

      const isGettingNewOrders =
        this._newOrders.size > 0 &&
        page === 1 &&
        this.searchValue === '' &&
        (this.sorting.column === null ||
          (this.sorting.column === OrdersHistoryTableColumns.CreatedAt && this.sorting.direction === SORTING_DIRECTION.DESCENDING));

      if (isGettingNewOrders) {
        this._newOrders.clear();
      }

      this._orders = ordersCast(data);
      this.pagination = paginationCast(pagination);
      this.ordersLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      if (ordersGetAbortController.signal.aborted) {
        this.ordersLoadingStatus = LoadingStatus.Success;
      } else {
        this.ordersLoadingStatus = LoadingStatus.Error;
        logger.error({ error, place: 'ordersHistoryStore _getOrders' });
      }
    }
  }).bind(this);

  @action public getOrders = () => {
    if (this.ordersLoadingStatus === LoadingStatus.Init) {
      this._getOrdersWithNoDelay(1);
    } else {
      this._getOrdersWithNoDelay(this.pagination.currentPage);
    }
  };

  @action private _getOrdersWithDelay = (pageNumber: number = 1) => {
    clearTimeout(this.ordersGettingTimeoutId);

    this.ordersGettingTimeoutId = setTimeout(() => {
      this._getOrders(pageNumber);
    }, 700);
  };

  @action private _getOrdersWithNoDelay = (pageNumber: number = 1) => {
    clearTimeout(this.ordersGettingTimeoutId);
    this._getOrders(pageNumber);
  };

  @action public handleSocketEvent = (socketEvent: TEventMessage<TSocketOrder, TOrderCRUDEvents>) => {
    const { data, event } = socketEvent;
    const socketOrder = orderCast({
      ...data,
      created_by: data.created_by_code,
      order_status: data.order_status_code,
      delivery_type: data.delivery_type_code,
      dinein_delivery_type: data.dinein?.dinein_delivery_type,
      is_curbside: data.curbside !== null,
      service_key: data.aggregator_key,
    });
    const orderExists = this._orders.has(socketOrder.id);

    const socketOrderInFilter = this.advancedFilter.orderInFilter({
      [AdvancedFilterProperties.Branch]: String(socketOrder.branch.id),
      [AdvancedFilterProperties.OrderType]: socketOrder.deliveryType,
      [AdvancedFilterProperties.OrderingChannel]: socketOrder.orderingChannel,
      [AdvancedFilterProperties.PaymentMethod]: socketOrder.paymentType,
      [AdvancedFilterProperties.Status]: socketOrder.status,
      [AdvancedFilterProperties.Date]: socketOrder.createdAt,
      [AdvancedFilterProperties.Time]: socketOrder.createdAt,
    });

    if (!socketOrderInFilter && this._newOrders.has(socketOrder.id)) {
      this._newOrders.delete(socketOrder.id);
    } else if (socketOrderInFilter && !orderExists) {
      this._newOrders.set(socketOrder.id, socketOrder);
    }

    if (event === 'updated' && orderExists) {
      if (socketOrderInFilter) {
        this._orders.set(socketOrder.id, {
          ...this._orders.get(socketOrder.id)!,
          status: socketOrder.status,
        });
      } else {
        this._orders.delete(socketOrder.id);
      }
    }
  };

  @action public getNewOrders = () => {
    this.sorting = defaultSorting;
    this.searchValue = '';
    this._newOrders.clear();
    this._getOrdersWithNoDelay(1);
  };

  @action public refreshLocalOrderById = () => {
    return;
  };

  @action public changeSorting = (column: OrdersHistoryTableColumns | undefined, direction: SORTING_DIRECTION) => {
    if (!column) return;

    this.sorting = { column, direction };
    this._getOrdersWithNoDelay();
  };

  @action public changeSearch = (searchValue: string = '') => {
    this.searchValue = searchValue;
    this._getOrdersWithDelay();
  };

  @action public changePage = (pageNumber: number) => {
    this._getOrdersWithNoDelay(pageNumber);
  };

  @action public downloadExportedDocument = flow(function* (this: OrdersHistoryStore): any {
    this.exportLoadingStatus = LoadingStatus.Loading;

    this.ordersExportAbortController.abort();
    const ordersExportAbortController = new AbortController();
    this.ordersExportAbortController = ordersExportAbortController;

    try {
      const config: AxiosRequestConfig = {
        signal: ordersExportAbortController.signal,
        params: this._makeFetchOrdersParams(window.IS_MOBILE ? 'url' : 'csv'),
      };

      if (window.IS_MOBILE) {
        const url = yield client.get('/v3/orders/history', config);
        yield wms.downloadFile(url);
      } else {
        yield downloadFile('/v3/orders/history', config);
      }

      this.exportLoadingStatus = LoadingStatus.Success;
    } catch (error: any) {
      if (ordersExportAbortController.signal.aborted) {
        this.exportLoadingStatus = LoadingStatus.Success;
      } else {
        this.exportLoadingStatus = LoadingStatus.Error;
        logger.error({ error, place: 'ordersHistoryStore downloadExportedDocument' });
      }
    }
  }).bind(this);

  @action handleAdvancedFilter = () => {
    this._getOrdersWithNoDelay();
  };

  @computed
  public get isOrdersNotFound() {
    return this.ordersLoadingStatus === LoadingStatus.Success && this._orders.size === 0;
  }

  @computed
  public get newOrdersCount() {
    return this._newOrders.size;
  }

  @computed
  public get orders() {
    return [...this._orders.entries()];
  }

  @action
  public clear = () => {
    this._orders = new Map<string, TOrder>();
    this.ordersLoadingStatus = LoadingStatus.Init;
  };
}

export const ordersHistoryStore = new OrdersHistoryStore();
